import { FocusCard, Account, Transaction } from '../types';

/**
 * services/cardEngine.ts
 * 
 * This service acts as the backend logic for the "Mindful Moments" feed.
 * It analyzes user data (Balances, Transaction History, Emotional State)
 * and generates a personalized list of Action Cards.
 */

// --- Mock Database for User Stats (Streaks, History) ---
// In a real app, this would be fetched from Firestore
const MOCK_USER_STATS = {
  streaks: {
    'check_in': 14,
    'payment_boost': 2,
    'pause_reflection': 5
  },
  lastInteractions: {
    'check_in': new Date().toISOString() // Simulating they haven't done it *today* yet if we compare dates properly
  }
};

/**
 * Core function to generate the deck of cards for the user
 */
export const generateDailyCards = (
  accounts: Account[], 
  transactions: Transaction[], 
  wellnessScore: number
): FocusCard[] => {
  const cards: FocusCard[] = [];

  // 1. ALWAYS ADD: Daily Check-In (The Foundation)
  cards.push(createCheckInCard());

  // 2. FINANCIAL LOGIC: The "Surplus" Trigger
  // If checking has healthy buffer (> $1000) AND they have debt, suggest a micro-payment.
  const checkingAccounts = accounts.filter(a => a.type === 'checking');
  const debtAccounts = accounts.filter(a => a.type === 'credit' || a.type === 'loan');
  
  const totalChecking = checkingAccounts.reduce((sum, a) => sum + a.balance, 0);
  const totalDebt = debtAccounts.reduce((sum, a) => sum + a.balance, 0);

  if (totalChecking > 1000 && totalDebt > 0) {
    cards.push(createPaymentCard(totalChecking));
  }

  // 3. BEHAVIORAL LOGIC: The "Regret" Trigger
  // If user has > 2 "regret" reflections in the last 10 transactions, trigger a restraint card.
  // Note: We need transactions that actually have reflections.
  const recentSkips = transactions
    .slice(0, 15)
    .filter(t => t.reflection === 'regret').length;

  if (recentSkips >= 2) {
    cards.push(createRestraintCard(recentSkips));
  } else if (wellnessScore < 50) {
    // Fallback if no skips but low wellness -> Offer Education/Comfort
    cards.push(createEducationCard());
  }

  return cards;
};

// --- Card Generators ---

const createCheckInCard = (): FocusCard => {
  return {
    id: 'daily_check_in',
    title: 'Morning Check In',
    description: 'Open the app without judgment. Just be here.',
    streak: MOCK_USER_STATS.streaks['check_in'],
    completedToday: false, // In real app, check DB if completed today
    category: 'mindset',
    type: 'check-in'
  };
};

const createPaymentCard = (checkingBalance: number): FocusCard => {
  // Dynamic amount: 1% of checking or $25, whichever is lower/safer
  const safeAmount = Math.floor(Math.min(checkingBalance * 0.05, 50));
  
  return {
    id: 'action_micro_payment',
    title: 'Micro-Payment',
    description: `You have a buffer. Send $${safeAmount} to your highest interest card?`,
    streak: MOCK_USER_STATS.streaks['payment_boost'],
    completedToday: false,
    category: 'action',
    type: 'financial-action',
    data: { amount: safeAmount }
  };
};

const createRestraintCard = (skipCount: number): FocusCard => {
  return {
    id: 'mindset_pause',
    title: 'The 24h Pause',
    description: `You've flagged ${skipCount} recent skips. Wait 24h before your next buy.`,
    streak: MOCK_USER_STATS.streaks['pause_reflection'],
    completedToday: false,
    category: 'restraint',
    type: 'behavioral-shift'
  };
};

const createEducationCard = (): FocusCard => {
  return {
    id: 'edu_biology',
    title: 'Biology of Stress',
    description: 'Why debt activates your fight-or-flight response.',
    streak: 0,
    completedToday: false,
    category: 'mindset',
    type: 'education'
  };
};

/**
 * Backend function to log interaction
 */
export const logCardInteraction = async (cardId: string, action: 'complete' | 'dismiss') => {
  // In a real app, this would write to Firestore

  
  // Update local mock stats (conceptually)
  if (cardId === 'daily_check_in') {
    MOCK_USER_STATS.streaks['check_in']++;
  }
};
